import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { useProfile } from 'context/profile';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { simpleDate } from 'lib/date';
import { capitalize } from 'lib/util';
import { SUBMITTED_DEMOGRAPHICS, SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow, { ActionStatus } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import PushToEhrErrorIcon from 'svg/PushToEhrErrorIcon';
import PushToEhrIcon from 'svg/PushToEhrIcon';
import PushToEhrProcessingIcon from 'svg/PushToEhrProcessingIcon';
import PushToEhrSuccessIcon from 'svg/PushToEhrSuccessIcon';
import PushToEhrWarningIcon from 'svg/PushToEhrWarningIcon';

import SyncPatientModal from './SyncPatientModal';

interface Props {
  action: SyncPatientToEhrAction;
  workflow: Workflow;
  // The FIRST send. Valid only while the action is `pending`/`action_required` — the API's
  // DISPATCHABLE_FROM.
  onDispatch: (actionId: string, outputs?: WorkflowValuesInput) => unknown;
  // Every send AFTER an attempt has finished — a retry, or a push to a newly selected facility. It
  // reruns first, so the vendor call rides a fresh instance (design 010). Two calls rather than one
  // because `errored` and `completed` are deliberately not dispatchable: one attempt per instance is
  // what lets the engine drop a late result from a superseded attempt.
  onResend: (action: SyncPatientToEhrAction, outputs?: WorkflowValuesInput) => unknown;
  isDispatching: boolean;
  // Persist the corrected demographics on the referral before sending. Values, not staged action
  // outputs: a corrected name is a fact about the patient and should survive this send (and outlive a
  // retry, which re-reads them). MANUAL provenance outranks the extraction's.
}

const PLATFORM_LABELS: Record<string, string> = {
  matrixcare: 'MatrixCare',
  pcc: 'PointClickCare',
};

export function platformLabel(platform: string | null): string {
  if (!platform) return 'the EHR';
  return PLATFORM_LABELS[platform] ?? platform;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// The ready/failed box's own button: Button's existing `xsmall` size already gives the 12px font
// (PR review) — only the tighter 4px/12px padding is new, and that stays a local override rather
// than joining `xsmall` itself. `xsmall` already has 10+ call sites across the app at the default
// 8px/24px padding; folding this padding into the shared size would restyle all of them for one
// component's layout.
const CompactButton = styled(Button)`
  padding: 4px 12px;
`;

// Every non-quiet state of this card (Figma 14573:4281) renders this same icon + heading/detail
// tinted box, with the action button pinned right on the same row when there is one — the
// uniformity is the point: a coordinator scanning the referral page sees one consistent shape for
// "what's happening with the EHR push" regardless of which of the seven states it's in.
const TONE_COLOR: Record<'blue' | 'purple' | 'green' | 'orange' | 'red', string> = {
  blue: colors.blue10,
  purple: colors.purple10,
  green: colors.green10,
  orange: colors.orange10,
  red: colors.red10,
};

const HeaderRow = styled.div<{ $tone: 'blue' | 'purple' | 'green' | 'orange' | 'red' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 4px;
  background: ${({ $tone }) => TONE_COLOR[$tone]};
  border: 1px solid ${({ $tone }) => TONE_COLOR[$tone]};
`;

const IconTextRow = styled.div<{ $alignTop?: boolean }>`
  display: flex;
  align-items: ${({ $alignTop }) => ($alignTop ? 'flex-start' : 'center')};
  gap: 16px;
`;

const TextStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// Every tinted box reads black100 (design review) — the tone lives in the box's background and its
// icon, not in the text color, so the text stays legible instead of a lower-contrast tint-on-tint.
const BannerHeading = styled(BodySmallBold)`
  color: ${colors.black100};
`;

const BannerText = styled(BodySmall)`
  color: ${colors.black100};
`;

// The vendor's verbatim sentence ("MedicalRecordNumber is required for this facility"), quoted and
// kept whole — paraphrasing it would strip the only actionable detail a coordinator gets.
const BannerPreWrapText = styled(BannerText)`
  white-space: pre-wrap;
`;

// landedElsewhere's note (below, inside the ready-to-send return) sits OUTSIDE a tinted box — it's
// a supplementary fact alongside the Ready banner, not a state of its own — so it keeps the plain
// warning color rather than the banners' black100.
const Note = styled(BodySmall)`
  color: ${colors.orangeHover};
`;

const DocList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DocItem = styled(BodySmall)<{ $status?: string | null }>`
  color: ${({ $status }) =>
    $status === 'imported' ? colors.green100 : $status === 'failed' ? colors.red100 : colors.black50};
`;

// Per-FILE import outcomes, not step statuses — so `skipped` keeps the word "Skipped" here even
// though the step status became "Not Applicable" (OLIO-3270). A file that was skipped really was
// passed over, and the row says why ("Face Sheet.png — Skipped: not a PDF"); "Not Applicable"
// would drop that fact on the floor.
const DOC_STATUS_LABEL: Record<string, string> = {
  imported: 'Sent',
  skipped: 'Skipped',
  failed: 'Failed',
};

// The per-file document import outcome (OLIO-2733). Renders nothing when the sync carried no documents.
// The platform reports each file by attachmentId only (the filename is PHI), so the name is resolved
// here from the workflow's own attachments.
function DocumentsSummary({ action, workflow }: { action: SyncPatientToEhrAction; workflow: Workflow }) {
  const documents = action.documents;
  if (!documents?.results?.length) return null;

  const nameFor = (id?: string | null) => workflow.attachments.find((a) => a.id === id)?.filename ?? 'Document';

  return (
    <div data-testid='sync-patient-documents'>
      <Detail>
        {documents.imported ?? 0} of {documents.total ?? documents.results.length} document
        {(documents.total ?? documents.results.length) === 1 ? '' : 's'} sent to the EHR
      </Detail>
      <DocList>
        {documents.results.map((result, index) => (
          <DocItem
            as='li'
            key={result.attachmentId ?? index}
            $status={result.status}
            data-testid={`sync-patient-document-${result.status ?? 'unknown'}`}>
            {nameFor(result.attachmentId)} — {DOC_STATUS_LABEL[result.status ?? ''] ?? result.status}
            {result.reason ? `: ${result.reason}` : ''}
          </DocItem>
        ))}
      </DocList>
    </div>
  );
}

// Outside any tinted box — DocumentsSummary's per-file breakdown, the only surviving caller now
// that the stale-facility notes read as banner text instead.
const Detail = styled(BodySmall)`
  color: ${colors.black50};
`;

/**
 * The Sync Patient to EHR card body (OLIO-2846).
 *
 * EVERY state below is derived from `action` + `workflow` — there is no useState here beyond the
 * modal's openness, and that is deliberate rather than incidental. The only thing that moves this
 * card is the server: a settle pass, or a refetch after the platform's callback. Local state would
 * survive a refetch and start disagreeing with it, and a half-sent card that says the wrong thing
 * about a real patient in a real EHR is the worst outcome available here.
 *
 * The referral is NOT terminal while this renders (design 010) — recording the outcome ends the
 * competition but the run completes by exhaustion, which this action is the last thing standing in
 * the way of. So the run is live, polling continues, and `rerun` works, which is what makes the
 * retry path a fresh instance rather than a second dispatch of the same one.
 *
 * Presentation reads `effectiveStatus`, behavior reads `status` — the ClinicalReviewPanel rule. The
 * two genuinely differ for this kind: duplicate/locked/matched/no_match come back `completed` and are
 * presented as `flagged`, so keying behavior off the presentation status would offer a Retry on a
 * push that actually succeeded.
 */
export default function SyncPatientBody({ action, workflow, onDispatch, onResend, isDispatching }: Props) {
  // The ONE piece of local state in this component, and only because a modal's openness is not a fact
  // about the referral. `reopenOnReview` distinguishes a fresh send (start at the document step) from
  // fixing a vendor rejection (go straight to the field it complained about).
  const [modal, setModal] = useState<null | 'fresh' | 'fix'>(null);
  const { profile } = useProfile();
  const { canEditAction } = useWorkflowPermissions();
  const platform = platformLabel(workflow.ehrSync.platform);
  const selectedFacilityId = workflow.valueFor('selected_facility_id')?.value ?? null;
  const facilityName = (id: string | null) => workflow.groups.find((group) => group.id === id)?.name ?? 'the facility';

  // AC 7 by CONTENT, not status — mirroring the API's guard. A landed sync whose recorded facility is
  // no longer the referral's winner is STALE, and the coordinator must be able to push to the new
  // building. Deriving it (rather than tracking a flag) is what makes the button come back on its own
  // when the winner changes: the value write invalidates the query, the card re-renders, done.
  //
  // `isSyncedSuccessfully`, NOT `isCompleted`. duplicate/locked/matched/no_match also arrive completed
  // and carry the facility they were ATTEMPTED against, so completion would read a refusal as a landed
  // record — and this branch's copy tells the coordinator a real patient is in that building and must
  // be discharged there by hand. A duplicate creates no resident at all, so that is a false statement
  // sending someone after a record nobody made. The API learned this same lesson as
  // SUCCESS_CLASSIFICATIONS; the FE must not re-derive it from status.
  // Read across the SIBLINGS, because the fact and the work now live on different instances. The sync
  // is per-building: after a push lands at A and the coordinator moves the winner to B, A's instance
  // holds the landed result and B's is the one that must be sent — and B is the instance this card
  // renders. Reading only `action` here would drop the warning entirely in exactly that case, which
  // is the case it exists for.
  //
  // Self is included so a run created before the per_group reshape — one shared, ungrouped instance —
  // still behaves. Publishing does not back-fill, so those runs exist.
  const landedElsewhere = workflow.actions
    .filter((sibling): sibling is SyncPatientToEhrAction => sibling.kind === action.kind)
    .find(
      (sibling) =>
        sibling.isSyncedSuccessfully &&
        !!sibling.syncedFacilityGroupId &&
        !!selectedFacilityId &&
        sibling.syncedFacilityGroupId !== selectedFacilityId
    );

  // This instance itself landed somewhere that is no longer the winner — the pre-reshape shape, where
  // one shared row carried both the result and the next push.
  const syncedElsewhere = landedElsewhere?.id === action.id;

  // Every path to a vendor write goes through the review modal — there is deliberately no direct dispatch here. A push
  // creates a real record in a real facility, and the demographics were extracted by an AI, so a person confirms them
  // at least once before the first attempt and again before every retry (the packet or the data may have changed since
  // the failure).
  //
  // Null without the grant, which is what takes every send off the card at once — this one helper is the only door to a
  // vendor write, so gating it here covers the first send, the Retry, the Fix and resend, and the stale-facility
  // re-push without each branch repeating the check. The branches themselves still render: a viewer should read that a
  // push failed, what the vendor said, and that the winner moved — those are facts about the referral. Only acting on
  // them needs the grant, matching ErroredPrompt. canEditAction, not blanket canEdit, because the kind
  // carries its own edit grant.
  // `compact` is passed explicitly by each call site rather than inferred from `label` — a label is
  // copy, and copy changes (this component has already renamed two of these buttons once); keying
  // layout off it would silently revert a button to full size the next time someone edits its text.
  const sendButton = (
    label: string,
    mode: 'fresh' | 'fix' = 'fresh',
    { compact = false }: { compact?: boolean } = {}
  ) => {
    if (!canEditAction(action, workflow)) return null;
    const ButtonComponent = compact ? CompactButton : Button;
    return (
      <ButtonComponent
        variant='primary'
        size={compact ? 'xsmall' : 'small'}
        disabled={isDispatching}
        onClick={() => setModal(mode)}
        data-testid='sync-patient-send'>
        {isDispatching ? 'Sending…' : label}
      </ButtonComponent>
    );
  };

  // Which call the modal's Send makes is a question about the ROW, not about which button opened the
  // modal: an instance that has already finished (errored, or completed against a facility that is no
  // longer selected) is not dispatchable, and every other state is. Deriving it here rather than
  // passing a flag down each branch is what stops a future branch from picking the wrong one.
  const needsFreshInstance = action.isErrored || action.isCompleted;

  const reviewModal = modal && (
    <SyncPatientModal
      workflow={workflow}
      action={action}
      startOnReview={modal === 'fix'}
      onClose={() => setModal(null)}
      onSend={async (values, outputs) => {
        // Corrections ride the dispatch and are recorded on the STEP — they are deliberately not
        // written back to the referral (OLIO-2846 QA follow-up). Writing them back is what QA hit:
        // these names are declared inputs of the auto-checks, so fixing a spelling on the way to the
        // EHR re-ran the denial list and the sex offender check. The referral keeps what extraction
        // found; the EHR gets what the coordinator confirmed.
        //
        // One request now instead of two, so a send can no longer half-apply.
        const staged = { ...outputs, ...(Object.keys(values).length > 0 && { [SUBMITTED_DEMOGRAPHICS]: values }) };
        return needsFreshInstance ? onResend(action, staged) : onDispatch(action.id, staged);
      }}
    />
  );

  // Quiet until the referral is decided. `pending` means the required inputs — the outcome and the
  // selected facility — are not there yet, so there is nothing to send and nobody owes a click; the
  // card renders its definition description instead (actionConfig drops `bodyWhenPending` for exactly
  // this). `action_required` is the ready-to-send state, not `pending`.
  if (action.isPending) return null;

  if (action.isRunning) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <HeaderRow $tone='purple'>
          <IconTextRow>
            <PushToEhrProcessingIcon />
            <TextStack>
              <BannerHeading>Pushing to {platform}…</BannerHeading>
              <BannerText>Olio is currently pushing this referral to {platform}.</BannerText>
            </TextStack>
          </IconTextRow>
        </HeaderRow>
      </Wrapper>
    );
  }

  // Errored splits on whether a push actually reached the vendor. With a message there is something
  // to fix and resend; without one we never got an answer, so all we can honestly offer is Retry.
  // Either way the send goes through `onResend` — `errored` is not dispatchable, so a retry is a new
  // instance. That is also why this branch survives the retry visually: the fresh instance renders as
  // ready-to-send rather than as a still-errored row, which is the honest reading of what happened.
  if (action.isErrored) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <HeaderRow $tone='red'>
          <IconTextRow>
            <PushToEhrErrorIcon />
            <TextStack>
              <BannerHeading data-testid='sync-patient-error'>Failed to send to {platform}.</BannerHeading>
              {/* The vendor's own words when we have them, preserving their line breaks. Without one (a
                  transport failure with no answer at all), the mockup's generic line is the honest
                  fallback rather than leaving the row silent about what "Failed" means. */}
              {action.vendorMessage ? (
                <BannerPreWrapText>{action.vendorMessage}</BannerPreWrapText>
              ) : (
                <BannerText>{platform} was unable to push records.</BannerText>
              )}
            </TextStack>
          </IconTextRow>
          {sendButton('Retry', 'fresh', { compact: true })}
        </HeaderRow>
        {reviewModal}
      </Wrapper>
    );
  }

  // A vendor answer a person can act on: the platform reports it action_required *with* the message
  // preserved. This is the missing-MRN loop and any other fixable field rejection.
  if (action.isActionRequired && action.hasResult) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <HeaderRow $tone='orange'>
          <IconTextRow>
            <PushToEhrWarningIcon />
            <TextStack>
              <BannerHeading>{platform} could not accept this patient yet.</BannerHeading>
              {action.vendorMessage && (
                <BannerPreWrapText data-testid='sync-patient-vendor-message'>{action.vendorMessage}</BannerPreWrapText>
              )}
            </TextStack>
          </IconTextRow>
          {sendButton('Fix and Push Again', 'fix', { compact: true })}
        </HeaderRow>
        {reviewModal}
      </Wrapper>
    );
  }

  if (action.isCompleted && !syncedElsewhere) {
    // PCC returns only a PENDING record — the facility must accept it before a patient exists there.
    // Saying "Synced" would tell the coordinator the work is done when it is not (AC 8).
    if (action.isPendingAcceptance) {
      return (
        <Wrapper data-testid='sync-patient-body'>
          <HeaderRow $tone='green'>
            <IconTextRow $alignTop>
              <PushToEhrSuccessIcon />
              <TextStack>
                <BannerHeading>Queued for Facility Acceptance</BannerHeading>
                <BannerText>
                  {/* Capitalized here, not by changing facilityName's "the facility" fallback globally —
                      every other call site uses it mid-sentence ("Synced to the facility..."), where it
                      must stay lowercase. This is the one description that opens a sentence with it. */}
                  {capitalize(facilityName(action.syncedFacilityGroupId))} must accept this patient in {platform} before
                  the record exists.
                </BannerText>
                {action.pendingPatientId && <BannerText>Pending Patient ID: {action.pendingPatientId}</BannerText>}
              </TextStack>
            </IconTextRow>
          </HeaderRow>
        </Wrapper>
      );
    }

    // duplicate / locked / matched / no_match arrive completed and present as flagged. For a PCC
    // `matched`, the platform sends the existing record's name + DOB (OLIO-2846) so the card names WHO
    // to reconcile in PCC rather than the bare "returned this patient for review". Every other flagged
    // case has no named matches and keeps the generic line plus whatever prose the vendor gave.
    if (action.effectiveStatus === ActionStatus.FLAGGED) {
      const matched = action.matchedPatients;
      if (matched.length === 0) {
        return (
          <Wrapper data-testid='sync-patient-body'>
            <HeaderRow $tone='orange'>
              <IconTextRow>
                <PushToEhrWarningIcon />
                <TextStack>
                  <BannerHeading>{platform} returned this patient for review.</BannerHeading>
                  {action.vendorMessage && (
                    <BannerPreWrapText data-testid='sync-patient-vendor-message'>
                      {action.vendorMessage}
                    </BannerPreWrapText>
                  )}
                </TextStack>
              </IconTextRow>
            </HeaderRow>
          </Wrapper>
        );
      }

      const count = action.matchedPatientCount;
      return (
        <Wrapper data-testid='sync-patient-body'>
          <HeaderRow $tone='orange'>
            <IconTextRow $alignTop>
              <PushToEhrWarningIcon />
              <TextStack>
                <BannerHeading>
                  {platform} found {count === 1 ? 'an existing patient' : `${count} existing patients`} matching these
                  demographics:
                </BannerHeading>
                {matched.map((patient) => {
                  const name =
                    [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim() || 'Unnamed patient';
                  const dob = patient.dob ? (simpleDate(patient.dob) ?? patient.dob) : null;
                  return (
                    <BannerText key={`${name}-${dob ?? ''}`} data-testid='sync-patient-matched'>
                      {name}
                      {dob ? `, DOB ${dob}` : ''}
                    </BannerText>
                  );
                })}
                {action.vendorMessage && (
                  <BannerPreWrapText data-testid='sync-patient-vendor-message'>
                    {action.vendorMessage}
                  </BannerPreWrapText>
                )}
                <BannerText>Review in {platform} before proceeding.</BannerText>
              </TextStack>
            </IconTextRow>
          </HeaderRow>
        </Wrapper>
      );
    }

    return (
      <Wrapper data-testid='sync-patient-body'>
        <HeaderRow $tone='green'>
          <IconTextRow $alignTop>
            <PushToEhrSuccessIcon />
            <TextStack>
              <BannerHeading data-testid='sync-patient-synced'>Pushed to {platform}</BannerHeading>
              {action.residentId && <BannerText>Resident ID: {action.residentId}</BannerText>}
              <DocumentsSummary action={action} workflow={workflow} />
            </TextStack>
          </IconTextRow>
        </HeaderRow>
      </Wrapper>
    );
  }

  // The winner moved after a successful push. Both facts matter: the patient IS in the old building
  // (nobody can un-sync them — neither vendor has an API for it, so someone has to discharge or
  // reject that record by hand), and the new building has not been synced yet.
  if (syncedElsewhere) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <HeaderRow $tone='orange'>
          <IconTextRow>
            <PushToEhrWarningIcon />
            <TextStack>
              <BannerHeading data-testid='sync-patient-stale-facility'>
                Pushed to {facilityName(action.syncedFacilityGroupId)}, which is no longer the selected facility.
              </BannerHeading>
              <BannerText>
                The record in {facilityName(action.syncedFacilityGroupId)} is not removed by syncing again — it must be
                discharged or rejected there.
              </BannerText>
            </TextStack>
          </IconTextRow>
          {sendButton(`Push to ${facilityName(selectedFacilityId)}`, 'fresh', { compact: true })}
        </HeaderRow>
        {reviewModal}
      </Wrapper>
    );
  }

  // Ready to send: `action_required` with nothing recorded yet — the state the evaluator activates
  // this action into once the decision lands.
  //
  // GATED EXPLICITLY rather than left as the fall-through. The fall-through was a catch-all for every
  // status not handled above, and `flagged` reaches it — verified by rendering each status through
  // CategoryCard: flagged renders this body, where canceled and pending render none. A flagged sync
  // therefore offered a live Send button, and `flagged` is NOT in the API's DISPATCHABLE_FROM, so the
  // click could only 422 — which is precisely the self-blocking button the kind's own comment warns
  // about when it explains why the demographics are click-time checks rather than required inputs.
  // Naming the ready state instead of negating the others also means a new status can't silently
  // inherit a Send button.
  if (!action.isActionRequired) return null;

  // Hidden when the client has no patient-writable integration — the API refuses the dispatch, so a
  // button that always 422s is worse than nothing. The kind's `applicable?` normally parks such a row
  // `skipped` before this renders at all; this stays as the belt to that braces.
  if (!workflow.ehrSync.platform) return null;

  // The coordinator has to be a member of the building to push a patient into its EHR. The API's
  // dispatch guard 403s otherwise, and onSendError can only read the 422 model-validation shape, so
  // that refusal surfaces as a bare "Something went wrong" toast (OLIO-3493) while the step parks
  // Not Applicable — the coordinator is left thinking it worked. Say it HERE, where they can act on
  // it, instead of offering a button whose only outcome is a rejected dispatch. Membership is checked
  // before the facility-identifier guard below: a user who can't access the building can't fix its
  // External ID either, so "you don't have access" is the message that fits them.
  if (selectedFacilityId && !profile.canAccessGroup(selectedFacilityId)) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        {/* Same tinted-box shape as the other ACTION_REQUIRED states (ActionCard's generic header is
            hidden for this status — see actionConfig's hideHeaderForStatuses — so this box is the
            row's only title). */}
        <HeaderRow $tone='orange'>
          <IconTextRow>
            <PushToEhrWarningIcon />
            <TextStack>
              <BannerHeading>Cannot Push to {platform}</BannerHeading>
              <BannerText data-testid='sync-patient-no-facility-access'>
                This referral was sent to {facilityName(selectedFacilityId)}. Only users with access to that facility
                can push this referral to their EHR.
              </BannerText>
            </TextStack>
          </IconTextRow>
        </HeaderRow>
      </Wrapper>
    );
  }

  // The building has to carry a vendor facility id of its own (its External ID or its Location's).
  // Without one, DispatchGuard refuses at the click with "has no EHR facility identifier" — so say so
  // HERE, where the coordinator can act on it, instead of offering a button whose only possible
  // outcome is a 422. This is what `ehrSync.facilityIds` is served for.
  if (selectedFacilityId && !workflow.ehrSync.facilityIds.includes(selectedFacilityId)) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        {/* Why the send is blocked is a fact about the referral and stays for everyone; what to do about it is
            addressed to whoever can do it, so it goes with the button. Same tinted-box shape as the
            other ACTION_REQUIRED states — see the comment on the facility-access branch above. */}
        <HeaderRow $tone='orange'>
          <IconTextRow>
            <PushToEhrWarningIcon />
            <TextStack>
              <BannerHeading>Cannot Push to {platform}</BannerHeading>
              <BannerText data-testid='sync-patient-no-facility-id'>
                {facilityName(selectedFacilityId)} has no {platform} facility identifier, so this patient cannot be sent
                there yet.
                {canEditAction(action, workflow) && ' Add the facility’s External ID in settings, then send.'}
              </BannerText>
            </TextStack>
          </IconTextRow>
        </HeaderRow>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid='sync-patient-body'>
      {/* A push landed at a building that is no longer the winner, and this instance is the new
          winner's. Both facts matter and neither is obvious: the patient IS in the old building
          (nobody can un-sync them — neither vendor has an API for it), and the new one has not been
          sent yet. Pre-reshape both facts sat on one row; now they sit on two, which is why this
          reads siblings. */}
      {landedElsewhere && (
        <Note data-testid='sync-patient-stale-facility'>
          Pushed to {facilityName(landedElsewhere.syncedFacilityGroupId)}, which is no longer the selected facility.
          That record is not removed by syncing again — it must be discharged or rejected there.
        </Note>
      )}
      <HeaderRow $tone='blue'>
        <IconTextRow>
          <PushToEhrIcon />
          <TextStack>
            <BannerHeading>
              Ready to send to {platform}
              {selectedFacilityId ? ` (${facilityName(selectedFacilityId)})` : ''}
            </BannerHeading>
            <BannerText>Push patient details to {platform}.</BannerText>
          </TextStack>
        </IconTextRow>
        {sendButton('Push', 'fresh', { compact: true })}
      </HeaderRow>
      {reviewModal}
    </Wrapper>
  );
}
